import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useQuoteBuilderApi, type QuoteBuilderApi } from './client'
import { customerSearchQueryOptions, quotesQueryOptions } from './queries'

/** Quote Builder screens the router warms once the current page has settled. */
export const quoteWorkspaceRoutes = ['/quotes', '/quotes/new', '/communications'] as const

/**
 * Warm the reads every quote screen opens with, one at a time, so a fresh page never competes
 * with a burst of background requests (demo `lib/workspace-preload.ts`).
 */
export async function preloadQuoteWorkspace(
  client: QueryClient,
  api: QuoteBuilderApi,
  cancelled = () => false,
) {
  const reads = [
    () => client.prefetchQuery(quotesQueryOptions(api)),
    () => client.prefetchQuery(customerSearchQueryOptions(api, '')),
  ]
  for (const read of reads) {
    if (cancelled()) return
    await read()
  }
}

/** Mount once in the Quote Builder area: waits for the query cache to go idle, then preloads. */
export function useQuoteWorkspacePreload() {
  const client = useQueryClient()
  const router = useRouter()
  const api = useQuoteBuilderApi()

  useEffect(() => {
    let cancelled = false
    let started = false
    let timer: number | undefined
    const schedule = () => {
      window.clearTimeout(timer)
      timer = window.setTimeout(() => {
        if (cancelled || started || client.isFetching()) return
        started = true
        void preloadQuoteWorkspace(client, api, () => cancelled)
        for (const to of quoteWorkspaceRoutes) void router.preloadRoute({ to }).catch(() => {})
      }, 100)
    }
    const unsubscribe = client.getQueryCache().subscribe(schedule)
    schedule()
    return () => {
      cancelled = true
      unsubscribe()
      window.clearTimeout(timer)
    }
  }, [api, client, router])
}
